import React from 'react'
import { ApplicationNote } from '../services/jobApplicationService'
import { MessageBox, useMessageBox } from './MessageBox'
import './NoteItem.css'

interface NoteItemProps {
  note: ApplicationNote
  onEdit: (note: ApplicationNote) => void
  onDelete: (noteId: string) => void
}

export const NoteItem: React.FC<NoteItemProps> = ({ note, onEdit, onDelete }) => {
  const { messageBox, showMessageBox } = useMessageBox()

  const formatTimestamp = (timestamp: string): string => {
    try {
      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) {
        return 'Just now'
      } else if (diffMins < 60) {
        return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
      } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
      } else if (diffDays < 7) {
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
      } else {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
          hour: 'numeric',
          minute: '2-digit'
        })
      }
    } catch {
      return timestamp
    }
  }

  const handleDelete = () => {
    showMessageBox({
      type: 'confirm',
      title: 'Delete note?',
      message: 'Are you sure you want to delete this note?',
      buttons: ['cancel', 'yes'],
      onButtonClick: (button) => {
        if (button === 'yes') {
          onDelete(note.id)
        }
      }
    })
  }

  return (
    <div className="note-item">
      <div className="note-item-header">
        <span className="note-item-timestamp">{formatTimestamp(note.timestamp)}</span>
        <div className="note-item-actions">
          <button
            className="note-item-edit"
            onClick={() => onEdit(note)}
            title="Edit note"
          >
            ✏️
          </button>
          <button
            className="note-item-delete"
            onClick={handleDelete}
            title="Delete note"
          >
            🗑️
          </button>
        </div>
      </div>
      
      <div className="note-item-content">
        {note.activity && (
          <div className="note-item-field">
            <span className="note-item-label">Activity:</span>
            <span className="note-item-value" title={note.activity}>
              {note.activity.length > 10 ? `${note.activity.substring(0, 10)}...` : note.activity}
            </span>
          </div>
        )}
        
        {note.contactPerson && (
          <div className="note-item-field">
            <span className="note-item-label">Contact:</span>
            <span className="note-item-value">{note.contactPerson}</span>
          </div>
        )}
        
        {note.contactInfo && (
          <div className="note-item-field">
            <span className="note-item-label">Contact Info:</span>
            <span className="note-item-value">{note.contactInfo}</span>
          </div>
        )}
      </div>
      <MessageBox {...messageBox} />
    </div>
  )
}
